// Like and comment controls for a diary entry.
//
// The like is OPTIMISTIC: the count flips locally on tap and reconciles with
// the server's answer, rolling back if the call fails. The route is
// idempotent, so a double tap can never double count.
//
// Icons come from the shared Icon component, never an emoji. That includes
// the like control: no heart glyph, no exception.
import { useState } from "react";
import { Icon, IconComment, IconHeart, IconHeartFilled } from "@traveldiary/ui";
import { apiClient } from "../lib/api";
import { useApp } from "../state/app";
import type { AuthoredMoment } from "../lib/types";

export interface FeedActionsProps {
  entry: AuthoredMoment;
  onOpenComments?(entry: AuthoredMoment): void;
}

export function FeedActions({ entry, onOpenComments }: FeedActionsProps) {
  const { t, applyLike } = useApp();
  const [busy, setBusy] = useState(false);
  const liked = entry.likedByMe ?? false;
  const likeCount = entry.likeCount ?? 0;
  const commentCount = entry.commentCount ?? 0;

  async function toggleLike(): Promise<void> {
    if (busy) return;
    setBusy(true);
    const nextLiked = !liked;
    // Optimistic: move the count now, reconcile or roll back after.
    applyLike(entry.id, { liked: nextLiked, likeCount: Math.max(0, likeCount + (nextLiked ? 1 : -1)) });
    try {
      const result = nextLiked
        ? await apiClient.likeMoment(entry.id)
        : await apiClient.unlikeMoment(entry.id);
      applyLike(entry.id, { liked: result.liked, likeCount: result.likeCount });
    } catch {
      applyLike(entry.id, { liked, likeCount });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="feed-actions">
      <button
        type="button"
        className={liked ? "feed-action feed-action-on" : "feed-action"}
        aria-pressed={liked}
        aria-label={liked ? t("feed.unlike") : t("feed.like")}
        onClick={() => void toggleLike()}
      >
        {/* Shape and fill carry the state, never colour alone: the outline
            heart becomes the filled heart. Drawn glyphs from the shared set,
            never an emoji. */}
        <Icon size={18}>{liked ? <IconHeartFilled /> : <IconHeart />}</Icon>
        <span className="feed-count">{likeCount}</span>
      </button>
      <button
        type="button"
        className="feed-action"
        aria-label={t("feed.comments")}
        onClick={() => onOpenComments?.(entry)}
        disabled={!onOpenComments}
      >
        <Icon size={18}>
          <IconComment />
        </Icon>
        <span className="feed-count">{commentCount}</span>
      </button>
    </div>
  );
}
